import { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { wsClient } from "../lib/ws-client";
import { useConnectionStore } from "../stores/connection";

interface LockInfo {
  userId: number;
  userName: string;
  instanceId: string;
}

export interface FieldHandle<T = string> {
  value: T;
  onChange: (val: T) => void;
  onFocus: () => void;
  onBlur: () => void;
  lockedBy: LockInfo | null;
  isEditable: boolean;
  isDirty: boolean;
}

interface UseLiveFormOptions {
  entity: string;
  entityId: number | undefined;
  topic: string;
  enabled?: boolean;
}

const DEBOUNCE_MS = 300;

export function useLiveForm({ entity, entityId, topic, enabled = true }: UseLiveFormOptions) {
  const status = useConnectionStore((s) => s.status);
  const instanceId = wsClient.instanceId;
  const [serverData, setServerData] = useState<Record<string, unknown>>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [locks, setLocks] = useState<Record<string, LockInfo>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const isConnected = status === "connected";
  const isActive = enabled && entityId !== undefined && isConnected;

  useEffect(() => {
    if (!isActive) return;

    const dataSub = wsClient.subscribe(topic, (data) => {
      if (data && typeof data === "object") {
        if ("action" in (data as Record<string, unknown>)) {
          const event = data as { action: string; data: unknown };
          if (event.data && typeof event.data === "object") {
            setServerData(event.data as Record<string, unknown>);
          }
        } else {
          setServerData(data as Record<string, unknown>);
        }
      }
    });

    const lockTopic = `locks.${entity}.${entityId}`;
    const lockSub = wsClient.subscribe(lockTopic, (data) => {
      if (data && typeof data === "object") {
        setLocks(data as Record<string, LockInfo>);
      }
    });

    const editTopic = `edits.${entity}.${entityId}`;
    const editSub = wsClient.subscribe(editTopic, (data) => {
      if (data && typeof data === "object") {
        const edit = data as { field: string; value: unknown; instanceId: string };
        if (edit.instanceId !== instanceId) {
          setServerData((prev) => ({ ...prev, [edit.field]: edit.value }));
        }
      }
    });

    return () => {
      wsClient.unsubscribe(dataSub);
      wsClient.unsubscribe(lockSub);
      wsClient.unsubscribe(editSub);
    };
  }, [topic, entity, entityId, instanceId, isActive]);

  // Recover orphaned edits on mount — apply onto serverData
  useEffect(() => {
    if (!isActive) return;

    wsClient
      .rpc<Record<string, unknown>>("field.getEdits", { entity, entityId })
      .then((edits) => {
        if (edits && Object.keys(edits).length > 0) {
          setServerData((prev) => ({ ...prev, ...edits }));
          setDirtyFields((prev) => {
            const next = new Set(prev);
            for (const key of Object.keys(edits)) next.add(key);
            return next;
          });
        }
      })
      .catch(() => {});
  }, [entity, entityId, isActive]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of Object.values(debounceTimers.current)) {
        clearTimeout(timer);
      }
    };
  }, []);

  const persistEdit = useCallback(
    (fieldName: string, value: unknown) => {
      if (!isActive) return;
      wsClient.rpc("field.edit", { entity, entityId, field: fieldName, value }).catch(() => {});
    },
    [entity, entityId, isActive]
  );

  const acquireLock = useCallback(
    (fieldName: string) => {
      if (!isActive) return;
      wsClient.rpc("lock.acquire", { entity, entityId, field: fieldName }).catch(() => {});
    },
    [entity, entityId, isActive]
  );

  const releaseLock = useCallback(
    (fieldName: string) => {
      if (!isActive) return;
      wsClient.rpc("lock.release", { entity, entityId, field: fieldName }).catch(() => {});
    },
    [entity, entityId, isActive]
  );

  const field = useCallback(
    <T = string>(fieldName: string): FieldHandle<T> => {
      const value = serverData[fieldName] as T;
      const lock = locks[fieldName] || null;
      const isLockedByOther = lock !== null && lock.instanceId !== instanceId;

      return {
        value,
        lockedBy: isLockedByOther ? lock : null,
        isEditable: !isLockedByOther,
        isDirty: dirtyFields.has(fieldName),
        onChange: (val: T) => {
          setServerData((prev) => ({ ...prev, [fieldName]: val }));
          setDirtyFields((prev) => {
            if (prev.has(fieldName)) return prev;
            const next = new Set(prev);
            next.add(fieldName);
            return next;
          });

          if (debounceTimers.current[fieldName]) {
            clearTimeout(debounceTimers.current[fieldName]);
          }
          debounceTimers.current[fieldName] = setTimeout(() => {
            persistEdit(fieldName, val);
          }, DEBOUNCE_MS);
        },
        onFocus: () => acquireLock(fieldName),
        onBlur: () => releaseLock(fieldName),
      };
    },
    [serverData, dirtyFields, locks, instanceId, persistEdit, acquireLock, releaseLock]
  );

  const isDirty = useMemo(() => dirtyFields.size > 0, [dirtyFields]);

  const submit = useCallback(async () => {
    if (!isActive || !isDirty) return;
    setIsSubmitting(true);
    try {
      const result = await wsClient.rpc("entity.save", { entity, entityId });
      setDirtyFields(new Set());
      return result;
    } finally {
      setIsSubmitting(false);
    }
  }, [entity, entityId, isActive, isDirty]);

  const discard = useCallback(async () => {
    if (!entityId) return;
    try {
      await wsClient.rpc("entity.discard", { entity, entityId });
    } catch (_) {}
    setDirtyFields(new Set());
  }, [entity, entityId]);

  return { field, submit, discard, isDirty, isSubmitting, serverData, isConnected };
}
